import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
    ClipboardList,
    KeyRound,
    MapPin,
    Clock,
    Sparkles,
    PlayCircle,
    CheckCircle2,
    PauseCircle,
    Pencil,
    ChevronDown,
    ChevronUp,
    AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import EmptyState from "@/components/EmptyState";
import { TASK_STATE_LABELS, TASK_TYPE_LABELS, PRIORITY_LABELS } from "@/lib/dictionaries";

function fmtH(min) {
    if (!min) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (m === 0) return `${h}h`;
    if (h === 0) return `${m}min`;
    return `${h}h ${m}min`;
}

function groupBy(tasks) {
    const today = new Date().toISOString().slice(0, 10);
    const today_g = [];
    const upcoming = [];
    const past = [];
    tasks.forEach((t) => {
        if (t.scheduled_date === today) today_g.push(t);
        else if (t.scheduled_date > today) upcoming.push(t);
        else past.push(t);
    });
    return { today_g, upcoming, past };
}

export default function OperatorTasks() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [editing, setEditing] = useState(null); // task being edited
    const [searchParams] = useSearchParams();
    const focusId = searchParams.get("focus");
    const [filter, setFilter] = useState("active"); // active | all | completed

    const load = () => {
        setLoading(true);
        api.get("/operator/tasks").then((r) => setTasks(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    useEffect(() => {
        if (focusId && !loading) {
            setExpanded((prev) => ({ ...prev, [focusId]: true }));
            setTimeout(() => {
                const el = document.querySelector(`[data-task-id="${focusId}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 200);
        }
    }, [focusId, loading]);

    const filtered = useMemo(() => {
        if (filter === "active") return tasks.filter((t) => ["pending", "in_progress", "delayed"].includes(t.state));
        if (filter === "completed") return tasks.filter((t) => ["completed", "validated"].includes(t.state));
        return tasks;
    }, [tasks, filter]);

    const groups = useMemo(() => groupBy(filtered), [filtered]);

    const setTaskState = async (task, newState) => {
        try {
            await api.patch(`/operator/tasks/${task.id}`, { state: newState });
            load();
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    return (
        <div className="space-y-5" data-testid="op-tasks-page">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">Trabajo asignado</p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">Mis tareas</h2>
                <p className="text-sm text-zentra-muted mt-1">{tasks.length} totales · {filtered.length} en este filtro</p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <FilterChip active={filter === "active"} onClick={() => setFilter("active")} label={`Activas (${tasks.filter((t) => ["pending", "in_progress", "delayed"].includes(t.state)).length})`} />
                <FilterChip active={filter === "completed"} onClick={() => setFilter("completed")} label="Completadas" />
                <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todas" />
            </div>

            {loading ? (
                <div className="zentra-skeleton h-72" />
            ) : filtered.length === 0 ? (
                <EmptyState icon={ClipboardList} title="Sin tareas" description="No tienes tareas en este filtro." />
            ) : (
                <div className="space-y-6">
                    {groups.today_g.length > 0 && (
                        <SectionGroup label="HOY" tasks={groups.today_g} expanded={expanded} setExpanded={setExpanded} onState={setTaskState} onEdit={setEditing} />
                    )}
                    {groups.upcoming.length > 0 && (
                        <SectionGroup label="PRÓXIMAS" tasks={groups.upcoming} expanded={expanded} setExpanded={setExpanded} onState={setTaskState} onEdit={setEditing} />
                    )}
                    {groups.past.length > 0 && (
                        <SectionGroup label="ANTERIORES" tasks={groups.past} expanded={expanded} setExpanded={setExpanded} onState={setTaskState} onEdit={setEditing} dim />
                    )}
                </div>
            )}

            <EditTaskModal task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
    );
}

function FilterChip({ active, onClick, label }) {
    return (
        <button type="button" onClick={onClick} className={`px-3 py-1.5 rounded-md text-xs font-medium border ${active ? "bg-zentra-primary text-white border-zentra-primary" : "bg-white text-zentra-ink border-zentra-border"}`}>
            {label}
        </button>
    );
}

function SectionGroup({ label, tasks, expanded, setExpanded, onState, onEdit, dim }) {
    return (
        <div className={dim ? "opacity-80" : ""}>
            <p className="text-[10px] font-bold tracking-widest text-zentra-muted uppercase mb-2 px-1">{label} · {tasks.length}</p>
            <div className="space-y-2">
                {tasks.map((t) => (
                    <TaskCard key={t.id} task={t} expanded={!!expanded[t.id]} onToggle={() => setExpanded((p) => ({ ...p, [t.id]: !p[t.id] }))} onState={onState} onEdit={onEdit} />
                ))}
            </div>
        </div>
    );
}

function TaskCard({ task, expanded, onToggle, onState, onEdit }) {
    const isActive = ["pending", "in_progress"].includes(task.state);
    return (
        <div className={`zentra-card-static overflow-hidden ${task.state === "in_progress" ? "ring-2 ring-amber-300" : ""}`} data-task-id={task.id} data-testid={`op-task-${task.id}`}>
            <div className="flex items-stretch">
                <button type="button" onClick={onToggle} className="flex-1 p-4 text-left flex items-center gap-3 hover:bg-zentra-bg/30 transition">
                    <div className="w-14 text-center shrink-0">
                        <p className="font-mono-zentra text-zentra-muted text-[10px] uppercase">hora</p>
                        <p className="font-display text-sm font-bold text-zentra-ink">{task.start_time || "—"}</p>
                        {task.end_time && <p className="font-mono-zentra text-[10px] text-zentra-muted">{task.end_time}</p>}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-display font-semibold text-zentra-ink truncate">{task.property?.name || "Propiedad"}</p>
                            {task.service_type_text && (
                                <span className="zentra-chip bg-zentra-accent/15 text-zentra-primary border-zentra-accent text-[10px]">{task.service_type_text}</span>
                            )}
                            {task.is_secondary && (
                                <span className="zentra-chip bg-violet-100 text-violet-800 border-violet-200 text-[10px]">2ª limpiadora</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zentra-muted mt-0.5 flex-wrap">
                            <span className="font-mono-zentra">{task.property?.code}</span>
                            <span>· {TASK_TYPE_LABELS[task.type] || task.type}</span>
                            <span>· {fmtH(task.estimated_minutes)}</span>
                            {task.key_access && <span className="inline-flex items-center gap-0.5"><KeyRound size={10} /> {task.key_access}</span>}
                        </div>
                    </div>
                    <StateBadge value={task.state} dict={TASK_STATE_LABELS} />
                    {expanded ? <ChevronUp size={16} className="text-zentra-muted" /> : <ChevronDown size={16} className="text-zentra-muted" />}
                </button>
                <Link
                    to={`/operario/tareas/${task.id}`}
                    className="bg-zentra-primary hover:bg-zentra-primary/90 text-white text-[11px] font-semibold uppercase tracking-widest px-4 flex items-center"
                    data-testid={`op-open-${task.id}`}
                >
                    Abrir
                </Link>
            </div>

            {expanded && (
                <div className="border-t border-zentra-border bg-zentra-bg/30 p-4 space-y-4">
                    {task.property?.address && (
                        <div className="flex items-start gap-2 text-sm">
                            <MapPin size={14} className="text-zentra-muted mt-0.5 shrink-0" />
                            <p className="text-zentra-ink">{task.property.address}</p>
                        </div>
                    )}
                    {task.property?.fixed_instructions && (
                        <div className="rounded-md bg-white border border-zentra-border p-3">
                            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-accent-strong mb-1.5">Instrucciones fijas de la propiedad</p>
                            <p className="text-xs text-zentra-ink whitespace-pre-wrap font-mono-zentra leading-relaxed">{task.property.fixed_instructions}</p>
                        </div>
                    )}
                    {task.day_instructions && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                            <p className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 mb-1.5 inline-flex items-center gap-1"><AlertCircle size={11} /> Instrucciones del día</p>
                            <p className="text-xs text-amber-900 whitespace-pre-wrap">{task.day_instructions}</p>
                        </div>
                    )}
                    {task.notes && (
                        <div className="rounded-md bg-zentra-bg border border-zentra-border p-3">
                            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted mb-1">Mis notas</p>
                            <p className="text-xs text-zentra-ink whitespace-pre-wrap">{task.notes}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zentra-border">
                        {task.state === "pending" && (
                            <button type="button" onClick={() => onState(task, "in_progress")} className="zentra-btn-primary text-xs" data-testid={`op-start-${task.id}`}>
                                <PlayCircle size={14} /> Empezar
                            </button>
                        )}
                        {task.state === "in_progress" && (
                            <>
                                <button type="button" onClick={() => onState(task, "completed")} className="zentra-btn-primary text-xs" data-testid={`op-complete-${task.id}`}>
                                    <CheckCircle2 size={14} /> Marcar completada
                                </button>
                                <button type="button" onClick={() => onState(task, "pending")} className="zentra-btn-ghost text-xs">
                                    <PauseCircle size={14} /> Pausar
                                </button>
                            </>
                        )}
                        {task.state === "completed" && (
                            <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                                <CheckCircle2 size={14} /> Completada
                            </span>
                        )}
                        <button type="button" onClick={() => onEdit(task)} className="zentra-btn-ghost text-xs ml-auto" data-testid={`op-edit-${task.id}`}>
                            <Pencil size={12} /> Notas / tiempo real
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function EditTaskModal({ task, onClose, onSaved }) {
    const [actualMin, setActualMin] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (task) {
            setActualMin(task.actual_minutes || "");
            setNotes(task.notes || "");
            setError("");
        }
    }, [task]);

    if (!task) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            const payload = { notes };
            if (actualMin !== "" && actualMin !== null) payload.actual_minutes = Number(actualMin);
            await api.patch(`/operator/tasks/${task.id}`, payload);
            onSaved();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    // Inline modal (full-screen on mobile, centered on desktop)
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="op-edit-modal">
            <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="font-display text-lg font-bold text-zentra-ink mb-1">Notas y tiempo real</h3>
                <p className="text-xs text-zentra-muted mb-4">{task.property?.name}</p>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="zentra-label">Tiempo real empleado (minutos)</label>
                        <input type="number" min="0" className="zentra-input" value={actualMin} onChange={(e) => setActualMin(e.target.value)} placeholder={String(task.estimated_minutes || 60)} data-testid="op-actual-min" />
                        <p className="text-[11px] text-zentra-muted mt-1">Estimado: {task.estimated_minutes || 60} min</p>
                    </div>
                    <div>
                        <label className="zentra-label">Notas para el equipo</label>
                        <textarea className="zentra-textarea min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Algo a destacar de la limpieza..." data-testid="op-notes" />
                    </div>
                    {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="zentra-btn-ghost">Cancelar</button>
                        <button type="submit" className="zentra-btn-primary" disabled={saving} data-testid="op-save-edit">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
